/**
 * Pagina lista globale lotti di magazzino.
 *
 * Mostra tutti i lotti del tenant con filtri per stato e unita' organizzativa
 * (negozio) e multi-selezione per l'attribuzione in blocco della UO
 * (cantiere lotti->UO, T5). Serve a Walter per attribuire lo storico lotti
 * (oggi tutto senza UO) ai negozi.
 *
 * Pattern multi-selezione: modules/email/frontend/src/components/MessaggiList.tsx
 * (Set<string> + checkbox riga + seleziona-tutti-pagina + barra azioni).
 * Filtri e paginazione in URL (regola HARD back-navigation): tornando dal
 * dettaglio lotto si ritrova la stessa pagina/filtri.
 *
 * Nomi articolo risolti per id univoco della pagina corrente (nessuna fetch
 * bulk nuova: LottoResponse espone solo articolo_id, stesso pattern gia'
 * usato da LottiTable/LottoDetail per posizione/fornitore).
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlNumberState, useUrlState } from '@lib/useUrlState';
import { useUnitaAttive } from '@lib/unitaOrganizzativa';
import { useFormatters } from '@lib/formatters';

/** Sentinella filtro stato/UO "tutti" (coerente con FornitoriList/FattureList). */
const TUTTI = '__all__';
/** Sentinella filtro UO "senza negozio" -> mappa su query param senza_uo=true. */
const SENZA_NEGOZIO = '__senza__';
/** Dimensione pagina di default (coerente con ArticoliBrowser). */
const PAGE_SIZE_DEFAULT = 20;

interface Lotto {
  id: string;
  articolo_id: string;
  numero_lotto: string | null;
  quantita_disponibile: number;
  data_scadenza: string | null;
  stato: string;
  unita_organizzativa_id: string | null;
}

interface ArticoloInfo {
  sku_interno: string;
  descrizione: string | null;
}

interface AttribuisciUoErrore {
  id: string;
  motivo: string;
}

interface AttribuisciUoEsito {
  ok: number;
  errori: AttribuisciUoErrore[];
}

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/** Variante Badge stato lotto (coerente con LottiTable/LottoDetail). */
const statoVariant = (
  stato: string,
): 'default' | 'destructive' | 'secondary' => {
  if (stato === 'disponibile') return 'default';
  if (stato === 'in_quarantena') return 'destructive';
  return 'secondary';
};

/** Label leggibile per lo stato del lotto. */
const statoLabel = (stato: string): string => {
  if (stato === 'disponibile') return 'Disponibile';
  if (stato === 'in_quarantena') return 'In quarantena';
  if (stato === 'esaurito') return 'Esaurito';
  if (stato === 'bloccato') return 'Bloccato';
  return stato;
};

/**
 * Pagina lista lotti con filtri stato/UO in URL e attribuzione UO in blocco.
 */
const LottiList: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();
  const { unita, loading: loadingUnita } = useUnitaAttive();

  const [lotti, setLotti] = useState<Lotto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nomiArticoli, setNomiArticoli] = useState<Record<string, ArticoloInfo>>({});

  // Filtri e paginazione in URL (regola HARD back-navigation).
  const [stato, setStato] = useUrlState('stato', TUTTI);
  const [uo, setUo] = useUrlState('uo', TUTTI);
  const [limit, setLimit] = useUrlNumberState('limit', PAGE_SIZE_DEFAULT);
  const [offset, setOffset] = useUrlNumberState('offset', 0);
  const [uoFiltroSearch, setUoFiltroSearch] = useState('');

  // Multi-selezione (locale, non in URL: coerente con MessaggiList).
  const [selezione, setSelezione] = useState<Set<string>>(new Set());
  const [uoBulkId, setUoBulkId] = useState('');
  const [uoBulkSearch, setUoBulkSearch] = useState('');
  const [assegnando, setAssegnando] = useState(false);
  const [esito, setEsito] = useState<AttribuisciUoEsito | null>(null);

  const nomeUo = useCallback(
    (id: string | null): string => {
      if (!id) return '-';
      return unita.find((u) => u.id === id)?.nome ?? id.slice(0, 8);
    },
    [unita],
  );

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (stato !== TUTTI) params.set('stato', stato);
      if (uo === SENZA_NEGOZIO) {
        params.set('senza_uo', 'true');
      } else if (uo !== TUTTI) {
        params.set('unita_organizzativa_id', uo);
      }
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      const resp = await fetch(`/api/v1/magazzino/lotti?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const data: Lotto[] = await resp.json();
      setLotti(data);
      setSelezione(new Set());

      // Risolvi nomi articolo per id univoco della pagina corrente.
      const ids = [...new Set(data.map((l) => l.articolo_id))];
      const risolti: Record<string, ArticoloInfo> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await fetch(`/api/v1/magazzino/articoli/${id}`, {
              headers: authHeaders(),
            });
            if (r.ok) {
              const a = await r.json();
              risolti[id] = {
                sku_interno: a.sku_interno,
                descrizione: a.descrizione ?? null,
              };
            }
          } catch {
            /* ignore: riga mostra id troncato come fallback */
          }
        }),
      );
      setNomiArticoli(risolti);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [stato, uo, limit, offset]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const articoloLabel = (l: Lotto): string => {
    const info = nomiArticoli[l.articolo_id];
    if (!info) return l.articolo_id.slice(0, 8);
    return info.descrizione ? `${info.sku_interno} - ${info.descrizione}` : info.sku_interno;
  };

  // -----------------------------------------------------------------------
  // Multi-selezione
  // -----------------------------------------------------------------------

  const idsPagina = lotti.map((l) => l.id);
  const tuttiSelezionati = idsPagina.length > 0 && idsPagina.every((id) => selezione.has(id));

  const toggleRiga = (id: string) => {
    const next = new Set(selezione);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelezione(next);
  };

  const toggleTutti = () => {
    if (tuttiSelezionati) setSelezione(new Set());
    else setSelezione(new Set(idsPagina));
  };

  const annullaSelezione = () => {
    setSelezione(new Set());
    setUoBulkId('');
    setUoBulkSearch('');
  };

  /** Conferma l'attribuzione in blocco della UO ai lotti selezionati. */
  const handleAttribuisci = async () => {
    if (assegnando || selezione.size === 0 || !uoBulkId) return;
    setAssegnando(true);
    setEsito(null);
    try {
      const resp = await fetch('/api/v1/magazzino/lotti/attribuisci-uo', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lotto_ids: [...selezione],
          unita_organizzativa_id: uoBulkId,
        }),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const data: AttribuisciUoEsito = await resp.json();
      setEsito(data);
      annullaSelezione();
      void carica();
    } catch (err) {
      setEsito({
        ok: 0,
        errori: [
          {
            id: '',
            motivo: err instanceof Error ? err.message : 'Errore sconosciuto',
          },
        ],
      });
    } finally {
      setAssegnando(false);
    }
  };

  // Opzioni Combobox UO (filtro pagina): include sentinelle "Tutti" / "Senza negozio".
  const opzioniUoFiltro: ComboboxOption[] = [
    { value: TUTTI, label: 'Tutti i negozi' },
    { value: SENZA_NEGOZIO, label: 'Senza negozio' },
    ...unita
      .filter((u) => {
        const q = uoFiltroSearch.trim().toLowerCase();
        if (!q) return true;
        return u.nome.toLowerCase().includes(q) || (u.codice ?? '').toLowerCase().includes(q);
      })
      .map<ComboboxOption>((u) => ({
        value: u.id,
        label: u.nome,
        sublabel: u.codice ?? u.tipo,
      })),
  ];

  const uoFiltroValue = (() => {
    // Mentre l'utente digita il testo battuto ha sempre precedenza: le
    // etichette sentinella si mostrano solo a ricerca vuota (stato quiescente
    // che riflette il filtro attivo in URL), altrimenti la ricerca digitata
    // sparirebbe al render successivo (bug: campo non digitabile).
    if (uoFiltroSearch !== '') return uoFiltroSearch;
    if (uo === TUTTI) return 'Tutti i negozi';
    if (uo === SENZA_NEGOZIO) return 'Senza negozio';
    const sel = unita.find((u) => u.id === uo);
    return sel ? sel.nome : '';
  })();

  const uoBulkValue = (() => {
    const sel = unita.find((u) => u.id === uoBulkId);
    return sel ? sel.nome : uoBulkSearch;
  })();

  const opzioniUoBulk: ComboboxOption[] = unita
    .filter((u) => {
      const q = uoBulkSearch.trim().toLowerCase();
      if (!q) return true;
      return u.nome.toLowerCase().includes(q) || (u.codice ?? '').toLowerCase().includes(q);
    })
    .map<ComboboxOption>((u) => ({
      value: u.id,
      label: u.nome,
      sublabel: u.codice ?? u.tipo,
    }));

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Lotti
        </h1>
        <p className="text-sm text-muted-foreground">
          Lotti di tutti gli articoli, con attribuzione al negozio (unita' organizzativa).
        </p>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select value={stato} onValueChange={(v) => { setStato(v); setOffset(0); }}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TUTTI}>Tutti gli stati</SelectItem>
                <SelectItem value="disponibile">Disponibile</SelectItem>
                <SelectItem value="in_quarantena">In quarantena</SelectItem>
                <SelectItem value="esaurito">Esaurito</SelectItem>
                <SelectItem value="bloccato">Bloccato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-64">
            <Combobox
              value={uoFiltroValue}
              onInputChange={(v: string) => {
                setUoFiltroSearch(v);
                if (uo !== TUTTI) setUo(TUTTI);
              }}
              onSelect={(opt: ComboboxOption) => {
                setUoFiltroSearch('');
                setUo(opt.value);
                setOffset(0);
              }}
              options={opzioniUoFiltro}
              placeholder="Cerca negozio..."
              loading={loadingUnita}
            />
          </div>
        </CardContent>
      </Card>

      {/* Barra azioni bulk (visibile solo con selezione attiva) */}
      {selezione.size > 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm font-medium">
              {selezione.size === 1 ? '1 selezionato' : `${selezione.size} selezionati`}
            </span>
            <div className="w-64">
              <Combobox
                value={uoBulkValue}
                onInputChange={(v: string) => {
                  setUoBulkSearch(v);
                  if (uoBulkId) setUoBulkId('');
                }}
                onSelect={(opt: ComboboxOption) => {
                  setUoBulkSearch('');
                  setUoBulkId(opt.value);
                }}
                options={opzioniUoBulk}
                placeholder="Attribuisci a negozio..."
                loading={loadingUnita}
              />
            </div>
            <Button
              size="sm"
              onClick={() => void handleAttribuisci()}
              disabled={assegnando || !uoBulkId}
            >
              {assegnando ? 'Attribuzione...' : 'Conferma'}
            </Button>
            <Button size="sm" variant="ghost" onClick={annullaSelezione} disabled={assegnando}>
              Annulla
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Esito ultima attribuzione */}
      {esito && (
        <Alert variant={esito.errori.length === 0 ? 'success' : 'warning'}>
          <AlertDescription>
            {esito.errori.length === 0
              ? `${esito.ok} lotti attribuiti con successo.`
              : `${esito.ok} ok, ${esito.errori.length} non attribuiti: ${esito.errori
                  .map((e) => e.motivo)
                  .join('; ')}`}
          </AlertDescription>
        </Alert>
      )}

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento...</p>
          )}
          {error && <p className="p-6 text-center text-sm text-destructive">{error}</p>}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={tuttiSelezionati}
                      ref={(el) => {
                        if (el) el.indeterminate = !tuttiSelezionati && selezione.size > 0;
                      }}
                      onChange={toggleTutti}
                      aria-label="Seleziona tutti i lotti della pagina"
                    />
                  </TableHead>
                  <TableHead>Lotto</TableHead>
                  <TableHead>Articolo</TableHead>
                  <TableHead className="text-right">Disponibile</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Negozio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lotti.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                      Nessun lotto trovato
                    </TableCell>
                  </TableRow>
                )}
                {lotti.map((lotto) => {
                  const selezionata = selezione.has(lotto.id);
                  return (
                    <TableRow
                      key={lotto.id}
                      className={`cursor-pointer hover:bg-muted/50 ${selezionata ? 'bg-primary/5' : ''}`}
                      onClick={() => navigate(`/magazzino/lotti/${lotto.id}`)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={selezionata}
                          onChange={() => toggleRiga(lotto.id)}
                          aria-label={`Seleziona lotto ${lotto.numero_lotto ?? lotto.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {lotto.numero_lotto ?? '-'}
                      </TableCell>
                      <TableCell className="text-sm">{articoloLabel(lotto)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {fmt.qty(lotto.quantita_disponibile)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {lotto.data_scadenza ? fmt.date(lotto.data_scadenza) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statoVariant(lotto.stato)}>{statoLabel(lotto.stato)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {lotto.unita_organizzativa_id ? (
                          nomeUo(lotto.unita_organizzativa_id)
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Paginazione: len(risultato) == limit -> possibile pagina successiva */}
          <div className="flex items-center justify-between border-t p-4 text-sm">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Precedente
            </Button>
            <span className="text-muted-foreground">
              Risultati {offset + 1}-{offset + lotti.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={lotti.length < limit || loading}
              onClick={() => setOffset(offset + limit)}
            >
              Successiva
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LottiList;
